import { AD_UNIT_MEDIATYPES } from '@pubstack/common/src/adFormat'
import { useDebouncedValue } from '@mantine/hooks'
import { type AdUnit, getAdUnitMediatypes, isDeviceEnabled } from '@pubstack/common/src/adunit'
import type { SiteStacksByProperty } from '@pubstack/common/src/stack'
import { type FunctionComponent, type ReactNode, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { Pagination } from '~/components/Pagination'
import { Qualifier } from '~/components/Qualifier'
import { Tooltip } from '~/components/Tooltip'
import Table, { type TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow, { ROW_CELL_CLASSES } from '~/components/table/TableRow'
import { Icon } from '~/components/Icon'
import { AdFormatIcon } from './AdFormatIcon'
import { Popover, PopoverContentWrapper, PopoverLine } from '~/components/Popover'
import { AdFormatMediatype } from './components/AdFormatCard'
import { PureAdmAdUnitDetailsFlyout } from './PureAdmAdUnitDetailsFlyout'
import { PureUsedStackTooltip } from '../../rules/PureUsedStackTooltip'

type PureAdmAdUnitsPageProps = {
  /** The current page of ad units, already narrowed and ordered server side. */
  adUnits: AdUnit[]
  sitesStacks?: SiteStacksByProperty
  isLoading: boolean
  /** Search, paging and ordering are the container's state — this page renders them and reports changes. */
  search: string
  onSearchChange: (search: string) => void
  page: number
  limit: number
  totalItems: number
  totalPages: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  sortBy: string
  sortOrder: 'ascending' | 'descending'
  onSortChange: (sortBy: string, sortOrder: 'ascending' | 'descending') => void
  onCreateAdUnit: () => void
  onUpdateAdUnit: (adUnitId: string) => void
  onArchiveAdUnit: (adUnit: AdUnit) => void
  onExportAdUnits: () => void
  onImportAdUnits: () => void
  onNavigateToArchived: () => void
  onOpenFlyout: () => void
}

/**
 * One row of controls, not two. The page chrome belongs to the tab shell now, and a second toolbar stacked
 * under the filters would cost a band of vertical space the table wants.
 *
 * The margin matches the filters' own, so the row keeps its height on an empty list, when they are gone.
 */
/** `my-3` is the filters' own margin, so the row keeps its height when the list is empty and they are not there. */
const TAB_ACTIONS_CLASSES = 'my-3 flex items-center justify-end gap-2'

/** `w-0` is what gets the last cell to shrink down and hug its action buttons. */
const ACTIONS_CELL_CLASSES = 'w-0'

const BUTTON_ACTIONS_CLASSES = 'flex justify-end gap-3'

const SEARCH_DEBOUNCE_TIME_MILLIS = 300

/** Cells stay narrow enough that a long ad unit name cannot push the actions off the row. */

/**
 * The width lives on the form, not on the input. The top actions row spreads its children apart, so this form is
 * only ever as wide as its content: a percentage set on the input resolved against that and collapsed the
 * box to its placeholder once the two derived filters that used to stretch the row went away. Sizing the
 * form against the toolbar and letting the input fill it keeps the 40%-capped-at-592px it was meant to have.
 */
const SEARCH_FORM_CLASSES = 'my-3 flex w-2/5 max-w-[592px] shrink-0 gap-3 [&>*]:w-full'

const TABLE_WITH_PAGINATION_CLASSES = 'flex flex-col gap-4'

/** The row above the table: search on one side, the buttons on the other. */
const TOP_ACTIONS_CLASSES = 'flex w-full items-center justify-between'

/** A span, where this used to be a paragraph nested in one, which is not valid. `block` keeps the line break. */
const TINY_TEXT_CLASSES = 'block font-roboto text-xs/[20px] font-normal text-slate-grey'

const DEVICE_ICONS_CLASSES = 'flex gap-2'

const SETTINGS_CLASSES = 'flex items-center gap-2'

export const PureAdmAdUnitsPage: FunctionComponent<PureAdmAdUnitsPageProps> = ({
  adUnits,
  sitesStacks,
  isLoading,
  search,
  onSearchChange,
  page,
  limit,
  totalItems,
  totalPages,
  onPageChange,
  onLimitChange,
  sortBy,
  sortOrder,
  onSortChange,
  onOpenFlyout,
  onCreateAdUnit,
  onUpdateAdUnit,
  onExportAdUnits,
  onImportAdUnits,
  onNavigateToArchived,
}) => {
  const columns: TableColumns<AdUnit> = [
    {
      name: 'Ad unit',
      isSortable: true,
      order: sortBy === 'name' ? sortOrder : 'none',
      attributeSort: 'name',
    },
    {
      name: 'Technical ID (div ID)',
      isSortable: true,
      order: sortBy === 'divId' ? sortOrder : 'none',
      attributeSort: 'divId',
    },
    {
      /* Not sortable any more. The order was `countDevices`, a score derived from the ad unit's devices, and
         the list is ordered in Mongo now — which can rank the stored field but not the score. A header that
         silently sorted on something else would be worse than one that does not sort. */
      name: 'Devices',
      isSortable: false,
    },
    {
      /* Same as Devices: `countSettings` is computed from header bidding and the ad unit's mediatypes. */
      name: 'Settings',
      isSortable: false,
    },
    {
      name: 'Used by',
      isSortable: false,
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ]

  const flyout = useGlobalFlyout()
  const { control } = useForm<{
    search: string
  }>({
    // Seeded from the URL so a shared link opens on the same search the sender was looking at.
    defaultValues: {
      search,
    },
  })
  const { canArchive, canCreate, canEdit, disabledReason } = useScopePermissions()
  const [actionsOpen, setActionsOpen] = useState(false)

  const typedSearch = useWatch({ control, name: 'search' })
  const [debouncedSearch] = useDebouncedValue(typedSearch, SEARCH_DEBOUNCE_TIME_MILLIS)

  useEffect(() => {
    if (debouncedSearch !== search) onSearchChange(debouncedSearch)
  }, [debouncedSearch])

  return (
    <div>
      {/* Adding an ad unit is the common act and keeps its own button. Export, import and the archive are the
          occasional ones and sit behind the kebab — the same table-level menu the media list uses. Help moved
          up beside the page title, where a question about the page belongs. */}
      <div className={TOP_ACTIONS_CLASSES}>
        {/* Only once there is a list to filter. The top actions row still spreads its children apart, so the
            actions stay where they are whether or not the filters are beside them. */}
        {(isLoading || totalItems > 0 || !!search) && (
          <form className={SEARCH_FORM_CLASSES}>
            {/* The Device and Mediatype filters are gone with the move to a server-side list: both were
                computed in the browser from the ad unit's formats, so neither survives a page that only ever
                holds twenty-five rows. Search covers the question they were mostly used to ask. */}
            <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
          </form>
        )}
        <div className={TAB_ACTIONS_CLASSES}>
          {/* Creating an ad unit is an owner's, like creating the media it will run on: it is inventory the
              whole scope deploys against. The button stays on screen saying why — seeing that the capability
              exists is half of knowing to ask an owner for it. */}
          {totalItems > 0 && (
            <Tooltip title={disabledReason} disabled={canCreate}>
              <Button iconName={'add_adUnit'} variant={'primary'} disabled={!canCreate} onClick={onCreateAdUnit} trackingLabel={'adm-ad-units-new'}>
                Add ad unit
              </Button>
            </Tooltip>
          )}
          <Popover
            align={'end'}
            open={actionsOpen}
            setOpen={setActionsOpen}
            trigger={<Button variant={'tertiary'} iconName={'more_vert'} title={'More actions'} trackingLabel={'adm-ad-units-actions-view'} />}
          >
            <PopoverContentWrapper>
              <PopoverLine
                onClick={() => {
                  setActionsOpen(false)
                  onExportAdUnits()
                }}
                data-tracking-label={'adm-ad-units-download'}
              >
                <Icon width={'20px'} name={'download'} />
                <span>Export ad units</span>
              </PopoverLine>
              {/* Importing is creating in bulk, so it takes the same owner flag as Add ad unit — but hidden
                  rather than disabled, like the archive entry below it: a kebab line that only ever leads to
                  Forbidden is worse than no line. The button above has the room to say why; a menu does not. */}
              {canCreate && (
                <PopoverLine
                  onClick={() => {
                    setActionsOpen(false)
                    onImportAdUnits()
                  }}
                  data-tracking-label={'adm-ad-units-upload'}
                >
                  <Icon width={'20px'} name={'upload'} />
                  <span>Import ad units</span>
                </PopoverLine>
              )}
              {/* The only way to the archived ad units. Owners only, because the page itself is owner-gated —
                  an entry everyone can see leading to a 403 is worse than no entry. */}
              {canArchive && (
                <PopoverLine
                  onClick={() => {
                    setActionsOpen(false)
                    onNavigateToArchived()
                  }}
                  data-tracking-label={'adm-ad-units-archived-navigate'}
                >
                  <Icon width={'20px'} name={'archive'} />
                  <span>Archived ad units</span>
                </PopoverLine>
              )}
            </PopoverContentWrapper>
          </Popover>
        </div>
      </div>
      {/* A search that matches nothing is still a populated tab: it gets the table and its "No results" row,
          not the empty-inventory illustration, which would read as "your ad units are gone". */}
      {isLoading || totalItems > 0 || !!search ? (
        <div className={TABLE_WITH_PAGINATION_CLASSES}>
          <Table
            columns={columns}
            isLoading={isLoading}
            onClickHeading={(column) => {
              const next = String(column.attributeSort)
              if (!next) return
              onSortChange(next, sortBy === next && sortOrder === 'ascending' ? 'descending' : 'ascending')
            }}
          >
            {/* A scope whose ad units are all archived lands here, not on the illustration: it has an inventory,
                it is just not showing any of it — and the archived link above is how it gets to the rest. */}
            {adUnits.length === 0 && (
              <TableRow>
                <NoResultsTableCell colspan={columns.length}>No results</NoResultsTableCell>
              </TableRow>
            )}
            {adUnits.map((adUnit) => (
              <TableRow
                key={adUnit.id}
                className={ROW_CELL_CLASSES}
                clickable={{
                  onClick: (event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    flyout.open(
                      PureAdmAdUnitDetailsFlyout,
                      { color: Colors.Turquoise, width: `816px`, closeTrackingLabel: 'adm-ad-units-detail-close' },
                      {
                        adUnit,
                        onEditAdUnit: (adUnitId) => {
                          flyout.close()
                          onUpdateAdUnit(adUnitId)
                        },
                        sitesStacks,
                      }
                    )
                    onOpenFlyout()
                  },
                  trackingLabel: 'adm-ad-units-view',
                }}
              >
                <TableCell>
                  <span className={'flex items-center gap-2'}>
                    <AdFormatIcon adFormatId={adUnit.adFormat?.id} />
                    <span>
                      {adUnit.name}
                      <span className={TINY_TEXT_CLASSES}>{adUnit.adServerAdUnitName}</span>
                    </span>
                  </span>
                </TableCell>
                <TableCell>{adUnit.divId}</TableCell>
                <TableCell>
                  <div className={DEVICE_ICONS_CLASSES}>
                    <Qualifier
                      iconName={'desktop'}
                      tooltipText={isDeviceEnabled('desktop', adUnit.devices) ? 'Desktop enabled' : 'Desktop disabled'}
                      color={Colors.Petrol}
                      active={isDeviceEnabled('desktop', adUnit.devices)}
                      enabled
                    />
                    <Qualifier
                      iconName={'mobile'}
                      tooltipText={isDeviceEnabled('mobile', adUnit.devices) ? 'Mobile enabled' : 'Mobile disabled'}
                      color={Colors.Pool}
                      active={isDeviceEnabled('mobile', adUnit.devices)}
                      enabled
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className={SETTINGS_CLASSES}>
                    <Qualifier
                      iconName={'header_bidding'}
                      tooltipText={adUnit.headerBiddingEnabled ? 'Header bidding enabled' : 'Header bidding disabled'}
                      color={Colors.Success}
                      active={adUnit.headerBiddingEnabled}
                      enabled
                    />
                    {getAdUnitMediatypes(adUnit).includes('Instream') ? (
                      <AdFormatMediatype key={'Instream'} isPresent={true}>
                        Instream
                      </AdFormatMediatype>
                    ) : (
                      AD_UNIT_MEDIATYPES.filter((mediatype) => mediatype !== 'Instream')
                        .map<ReactNode>((mediatype) => (
                          <AdFormatMediatype key={mediatype} isPresent={getAdUnitMediatypes(adUnit).includes(mediatype)}>
                            {mediatype}
                          </AdFormatMediatype>
                        ))
                        .reduce((prev, curr) => [prev, ' | ', curr])
                    )}
                  </div>
                </TableCell>
                <TableCell>{sitesStacks?.[adUnit.id] ? <PureUsedStackTooltip stacksBySite={sitesStacks[adUnit.id]} /> : <span>0 stack</span>}</TableCell>
                <TableCell className={ACTIONS_CELL_CLASSES}>
                  <div className={BUTTON_ACTIONS_CLASSES}>
                    <Tooltip title={'View'}>
                      <Button
                        iconName={'view'}
                        variant={'tertiary'}
                        title={'View'}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          flyout.open(
                            PureAdmAdUnitDetailsFlyout,
                            { color: Colors.Turquoise, width: '816px', closeTrackingLabel: 'adm-ad-units-detail-close' },
                            {
                              adUnit,
                              onEditAdUnit: (adUnitId) => {
                                flyout.close()
                                onUpdateAdUnit(adUnitId)
                              },
                              sitesStacks,
                            }
                          )
                          onOpenFlyout()
                        }}
                        trackingLabel={'adm-ad-units-icon-view'}
                      />
                    </Tooltip>
                    {/* The pen, now that renaming is gone: with one edit action left on the row there is
                        nothing for `tune` to be told apart from, and the pen is what says "edit" everywhere
                        else in the app. The name travels in the same PUT as the settings, so renaming meets
                        this gate too. */}
                    <Tooltip title={canEdit ? 'Edit settings' : disabledReason}>
                      <Button
                        iconName={'edit'}
                        variant={'tertiary'}
                        title={'Edit settings'}
                        disabled={!canEdit}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          onUpdateAdUnit(adUnit.id)
                        }}
                        trackingLabel={'adm-ad-units-edit'}
                      />
                    </Tooltip>
                    {/* Archiving a web ad unit is hidden here, owners and admins included — see the
                        "Archived ad units" entry above for the same call on the nav link. */}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </Table>

          <Pagination currentPage={page} totalPages={totalPages} limit={limit} totalItems={totalItems} onPageChange={onPageChange} onLimitChange={onLimitChange} />
        </div>
      ) : (
        <IllustrationMessage
          iconName={'night_sky'}
          iconSize={'190px'}
          title={'It seems that no ad unit has been connected to your inventory.'}
          message={'Add your ad units to the inventory so you can monetise them through Pubstack.'}
        >
          <Tooltip title={disabledReason} disabled={canCreate}>
            <Button iconName={'add_adUnit'} variant={'primary'} disabled={!canCreate} onClick={onCreateAdUnit} trackingLabel={'adm-ad-units-new'}>
              Add ad unit
            </Button>
          </Tooltip>
        </IllustrationMessage>
      )}
    </div>
  )
}
